import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Link } from "react-router-dom";

const SAFETY_FAQS = [
  {
    q: "What are the main risks at waterfalls?",
    a: "Drowning, slipping on wet rocks, vertigo near cliff edges, flash floods after heavy rain, cold-water shock, and animal hazards depending on region. The most common waterfall injuries come from slipping on moss-covered rocks; the most common deaths come from people entering the water at or above the falls.",
  },
  {
    q: "Can I swim under a waterfall?",
    a: "No. Avoid swimming directly under waterfalls. Falling debris can cause injury, and water turbulence at the base creates undercut rock ledges and undertows that can trap swimmers below the surface. If you're going to swim in a plunge pool, enter slowly and feet-first away from the cascade — never jump or dive.",
  },
  {
    q: "Is it safe to visit waterfalls after heavy rain?",
    a: "Take extra care. Rushing water often makes crossing or swimming near the falls unsafe — strong currents and hidden undercurrents develop quickly. Rivers can swell in minutes and access trails can wash out. If heavy rain is forecast or has just fallen, postpone your visit or seek advice from local emergency services.",
  },
  {
    q: "Should I jump in from the rocks above?",
    a: "Never jump into waterfalls from heights. Submerged rocks, branches and debris are impossible to see from above, and water depth changes constantly. Always enter pools slowly and feet-first, after carefully checking the bottom.",
  },
  {
    q: "What about cold water shock?",
    a: "Even on warm days, water temperatures at the base of waterfalls can be very cold. Sudden immersion can trigger cold-water shock — involuntary gasping, hyperventilation, and rapid loss of swimming ability. Acclimatise slowly, never jump in, and limit time in cold water.",
  },
  {
    q: "What shoes should I wear?",
    a: "Sturdy closed-toe shoes with grippy soles — hiking boots or trail runners. Avoid thongs, sandals, or smooth-soled trainers. Most waterfall injuries come from slipping on wet rocks, and the right footwear is your best defence.",
  },
  {
    q: "Is it OK to swim alone?",
    a: "Never swim alone. Many waterfalls are remote with patchy reception and slow emergency response times. Always swim with someone who can call for help if something goes wrong. Avoid alcohol — it impairs decision-making and dramatically increases the risk of accidents.",
  },
  {
    q: "Should I worry about wildlife?",
    a: "Animal hazards are site-specific. In northern Australia, watch for crocodiles — even in pools above waterfalls. In bush areas worldwide, look out for snakes. Light-coloured clothing makes leeches, ticks and march flies easier to spot. Always research local wildlife before visiting a new region.",
  },
  {
    q: "What should I always bring?",
    a: "Drinking water (waterfall water isn't always safe to drink), sun protection, insect repellent, sturdy shoes, a phone with offline maps, a small first-aid kit and a whistle for remote sites. Tell someone where you're going and when you'll be back.",
  },
  {
    q: "What if there's no phone reception?",
    a: "Most remote waterfalls have patchy or no reception. Download offline maps before you go, share your itinerary and expected return time with someone at home, and consider a personal locator beacon (PLB) for backcountry trips.",
  },
  {
    q: "Should I check anything before I leave home?",
    a: "Yes — always check park alerts or closures, recent weather, and forecast conditions for the next 24 hours. Many parks publish a 'walk closed' list. Heavy rain in the catchment area upstream can affect a waterfall even when conditions look fine where you're standing.",
  },
];

export default function SafetyAccordion({ siteSpecific = null }) {
  const items = siteSpecific
    ? [{ q: "Site-specific safety information", a: siteSpecific }, ...SAFETY_FAQS]
    : SAFETY_FAQS;
  return (
    <section className="py-12" data-testid="safety-accordion">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-10 lg:gap-16">
        <div>
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-stone-900">Safety information</h2>
          <p className="mt-4 text-stone-600 leading-relaxed">
            Waterfalls can be unforgiving. Read these before you go — and please, use your judgement on the day.
          </p>
          <Link to="/safety" className="mt-5 inline-block text-stone-900 font-semibold underline underline-offset-4" data-testid="safety-accordion-cta">
            Full safety guide →
          </Link>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {items.map((item, i) => (
            <AccordionItem key={i} value={`safety-${i}`} className="border-b border-stone-200">
              <AccordionTrigger className="text-left text-[15px] font-semibold text-stone-900 py-5 hover:no-underline" data-testid={`safety-q-${i}`}>
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-stone-700 leading-relaxed pb-5 text-[15px]" data-testid={`safety-a-${i}`}>
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
